import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Grid,
  Checkbox,
  ListItemText,
  Skeleton,
} from "@mui/material";
import { lineElementClasses, axisClasses } from "@mui/x-charts";
import InfoPopover from "../../../Components/Common/poper/InfoPopover";
import { useSelector } from "react-redux";
import ApiService from "../../../utils/fetch";
import { styled } from "@mui/material/styles";
import PlantTable from "./PlantTable";
import BottlerTable from "./BottlerTable";
import CustomerCountryTable from "./CustomerCountryTable";
import FrequentErrorGraph from "./FrequentErrorGraph";

// Custom pill container for sliding highlight
const PillContainer = styled("div")(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
  borderRadius: 20,
  background: "#f5f5f5",
  height: 32,
  minWidth: 180,
  alignItems: "center",
  boxShadow: "0 0 0 1px #ddd",
}));

const PillHighlight = styled("div")(({ selected }) => ({
  position: "absolute",
  top: 2,
  left: selected === "1" ? 2 : "50%",
  width: "calc(50% - 4px)",
  height: "calc(100% - 4px)",
  background: "#222",
  borderRadius: 16,
  transition: "left 0.25s cubic-bezier(.4,0,.2,1)",
  zIndex: 1,
}));

const PillLabel = styled("div")(({ active }) => ({
  position: "relative",
  zIndex: 2,
  flex: 1,
  textAlign: "center",
  fontSize: "0.85rem",
  fontWeight: active ? 500 : 400,
  color: active ? "#fff" : "#222",
  cursor: "pointer",
  padding: "0 8px",
  userSelect: "none",
  transition: "color 0.2s",
  lineHeight: "28px",
}));

// Aggregate pill styles
const AggregatePillContainer = styled("div")(({ theme }) => ({
  position: "relative",
  display: "inline-flex",
  borderRadius: 20,
  background: "#f5f5f5",
  height: 32,
  minWidth: 120,
  alignItems: "center",
  boxShadow: "0 0 0 1px #ddd",
  marginLeft: 12,
}));

const AggregatePillHighlight = styled("div")(({ selected }) => ({
  position: "absolute",
  top: 2,
  left: selected === "plant" ? 2 : "50%",
  width: "calc(50% - 4px)",
  height: "calc(100% - 4px)",
  background: "#222",
  borderRadius: 16,
  transition: "left 0.25s cubic-bezier(.4,0,.2,1)",
  zIndex: 1,
}));

const AggregatePillLabel = styled("div")(({ active }) => ({
  position: "relative",
  zIndex: 2,
  flex: 1,
  textAlign: "center",
  fontSize: "0.85rem",
  fontWeight: active ? 500 : 400,
  color: active ? "#fff" : "#222",
  cursor: "pointer",
  padding: "0 8px",
  userSelect: "none",
  transition: "color 0.2s",
  lineHeight: "28px",
}));

const DashboardCharts = ({ aggregate }) => {
  const [selectedOption, setSelectedOption] = useState("last12"); // Default to "Past Year"
  const [selectedMonths, setSelectedMonths] = useState([]);
  const [aggregateFilter, setAggregateFilter] = useState("plant");
  const [term, setTerm] = useState("1");

  // Add state for month list and loading
  const [monthList, setMonthList] = useState([]);
  const [monthListLoading, setMonthListLoading] = useState(true);
  const [monthCount, setMonthCount] = useState(9); // Make month count a variable

  const chartSx = {
    [`& .${axisClasses.root}`]: {
      stroke: "#888",
    },
    [`& .${lineElementClasses.root}`]: {
      strokeWidth: 2,
    },
  };

  // Fetch N months from API for dropdown
  useEffect(() => {
    const fetchMonths = async () => {
      setMonthListLoading(true);
      try {
        const response = await ApiService.get(
          `/v1/metrics/getPastNMonths/${monthCount}`
        );
        const months = response.data?.months || [];
        setMonthList(months);
        // Default to "This Month"
        setSelectedOption("last12");
      } catch (error) {
        setMonthList([]);
        setSelectedOption("last12");
      } finally {
        setMonthListLoading(false);
      }
    };
    fetchMonths();
  }, [monthCount]);

  // Update selectedMonths when selectedOption or monthList changes
  useEffect(() => {
    if (!monthList || monthList.length === 0) {
      setSelectedMonths([]);
      return;
    }
    let monthsToShow = [];
    switch (selectedOption) {
      case "thisMonth":
        monthsToShow = [monthList[0]];
        break;
      case "last3":
        monthsToShow = monthList.slice(1, 4); // Exclude current month
        break;
      case "last6":
        monthsToShow = monthList.slice(1, 7); // Exclude current month
        break;
      case "last12":
        monthsToShow = monthList.slice(1, 13); // Exclude current month
        break;
      default:
        monthsToShow = [monthList[0]];
    }
    setSelectedMonths(monthsToShow);
  }, [selectedOption, monthList]);

  // Automatically update aggregateFilter if aggregate prop changes (e.g., when switching tabs)
  useEffect(() => {
    if (aggregate && aggregate !== aggregateFilter) {
      setAggregateFilter(aggregate);
    }
  }, [aggregate]);

  return (
    <>
      <div className="container">
        {/* <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
           <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" align="left" gutterBottom="true">
              Error Performance
            </Typography>
            <InfoPopover margin="0 0 3px 0" />
          </Box> 
           Download button moved to top right 
        </Stack> */}

        {/* Filters OUTSIDE of chart/table, below title row */}
        <Box
          sx={{
            mb: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* Replace multi-select with single-select for 4 options */}
            <FormControl
              size="small"
              sx={{
                border: "1px solid black",
                borderRadius: 5,
                px: 0.25,
                minWidth: 160,
                height: 28,
                backgroundColor: "#fff",
              }}
            >
              {monthListLoading ? (
                <Skeleton variant="rectangular" width={160} height={28} />
              ) : (
                <Select
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                    MenuProps={{
                      disableScrollLock: true, // Disable scroll lock for the Select's dropdown menu
                    }}
                  sx={{
                    fontSize: "0.8rem",
                    padding: "6px 8px",
                    ".MuiOutlinedInput-input": {
                      padding: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                    },
                    ".MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      border: "none",
                    },
                  }}
                >
                  <MenuItem value="thisMonth">Current Month</MenuItem>
                  <MenuItem value="last3">Past 3 Months</MenuItem>
                  <MenuItem value="last6">Past 6 Months</MenuItem>
                  <MenuItem value="last12">Past Year</MenuItem>
                </Select>
              )}
            </FormControl>
            <PillContainer>
              <PillHighlight selected={term} />
              <PillLabel
                active={term === "1"}
                onClick={() => setTerm("1")}
                style={{ fontWeight: term === "1" ? 500 : 400 }}
              >
                Short Term
              </PillLabel>
              <PillLabel
                active={term === "2"}
                onClick={() => setTerm("2")}
                style={{ fontWeight: term === "2" ? 500 : 400 }}
              >
                Long Term
              </PillLabel>
            </PillContainer>
          </Box>
          {/* Align aggregate pill selector to the right */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AggregatePillContainer>
              <AggregatePillHighlight selected={aggregateFilter} />
              <AggregatePillLabel
                active={aggregateFilter === "plant"}
                onClick={() => setAggregateFilter("plant")}
              >
                Plant
              </AggregatePillLabel>
              <AggregatePillLabel
                active={aggregateFilter === "bottler"}
                onClick={() => setAggregateFilter("bottler")}
              >
                Bottler
              </AggregatePillLabel>
            </AggregatePillContainer>
          </Box>
        </Box>

        <Stack
          direction={{ xs: "column", md: "row", lg: "row" }}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 3, lg: 4 },
            alignItems: { xs: "center", md: "flex-start" },
            justifyContent: { xs: "center", md: "space-between" },
          }}
        >
          {/* <Stack
          direction={{ xs: 'column', md: 'row' }}
          gap={{ xs: 4, md: 10 }}
          alignItems={{ xs: 'center', md: 'flex-start' }}
          justifyContent={{ xs: 'center', md: 'space-between' }}
        > */}

          {/* Graph */}
          <Grid
            item
            xs={12}
            md={4}
            lg={3}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              maxWidth: { xs: "100%", md: 300, lg: 500 },
              mx: "auto",
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Most Frequent Errors
            </Typography>
            <FrequentErrorGraph selectedMonths={selectedMonths} term={term} />
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={1}
            >
              {/* Remove duplicate title below the graph */}
            </Box>
          </Grid>
          {/* Table(s) */}
          <Grid
            item
            xs={12}
            md={7.5}
            lg={9}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              mx: "auto",
              maxWidth: { xs: "100%", md: 450, lg: 700, xl:800 },
            }}
          >
            <Box
              className="container"
              sx={{ flex: 2, minWidth: 350, overflow: "auto", width: "100%" }}
            >
              {aggregateFilter === "plant" ? (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    Error Performance Details (By Plant)
                  </Typography>
                  <PlantTable selectedMonths={selectedMonths} term={term} />
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: 500, mt: 3 }}
                  >
                    Error Performance Details (By Bottler)
                  </Typography>
                  <BottlerTable selectedMonths={selectedMonths} term={term} />
                </>
              ) : (
                <>
                  <Typography
                    variant="subtitle1"
                    sx={{ mb: 1, fontWeight: 500 }}
                  >
                    Error Performance Details (By Customer Country)
                  </Typography>
                  <CustomerCountryTable
                    selectedMonths={selectedMonths}
                    term={term}
                  />
                </>
              )}
            </Box>
          </Grid>
        </Stack>
      </div>
    </>
  );
};

export default DashboardCharts;
